import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Wrench, 
  DollarSign, 
  FileText, 
  Clock, 
  Shield,
  CheckCircle,
  AlertCircle,
  Eye,
  Calendar,
  Banknote,
  MapPin,
  Truck,
  Phone,
  Users
} from 'lucide-react';
import HelicopterSilhouetteIcon from '@/components/icons/HelicopterSilhouetteIcon';
import { BlurredPrivateData } from '@/components/privacy/BlurredPrivateData';

interface DryLeaseDetailsDisplayProps {
  // Pricing fields
  hourlyRate?: number;
  monthlyRate?: number;
  depositRequired?: number;
  minimumPeriod?: number;
  maximumPeriod?: number;
  // Availability
  availability?: string[];
  availableFromDate?: string;
  availableUntilDate?: string;
  // Delivery options
  deliveryOptions?: string[];
  // Operational terms
  maintenanceResponsibility?: string;
  partsRepairsPayment?: string;
  returnConditions?: string;
  maxUsageHoursPerMonth?: number;
  insuranceCoverageRequired?: string;
  registrationRequirements?: string;
  inspectionRights?: string;
  earlyTerminationFees?: string;
  // Operational requirements
  pilotQualifications?: string[];
  insuranceRequirements?: string[];
  operatingLimitations?: string[];
  // Emergency & support
  emergencyContact?: string;
  breakdownProcedures?: string;
  dryLeaseTerms?: any;
  hidePrice?: boolean;
  onRequestInfo?: () => void;
  className?: string;
}

const DryLeaseDetailsDisplay: React.FC<DryLeaseDetailsDisplayProps> = ({
  hourlyRate,
  monthlyRate,
  depositRequired,
  minimumPeriod,
  maximumPeriod,
  availability,
  availableFromDate,
  availableUntilDate,
  deliveryOptions,
  maintenanceResponsibility,
  partsRepairsPayment,
  returnConditions,
  maxUsageHoursPerMonth,
  insuranceCoverageRequired,
  registrationRequirements,
  inspectionRights,
  earlyTerminationFees,
  pilotQualifications,
  insuranceRequirements,
  operatingLimitations,
  emergencyContact,
  breakdownProcedures,
  dryLeaseTerms,
  hidePrice = false,
  onRequestInfo,
  className = ''
}) => {
  // Extract pricing from dryLeaseTerms if not passed directly
  const hourly = hourlyRate || dryLeaseTerms?.hourlyRate;
  const monthly = monthlyRate || dryLeaseTerms?.monthlyRate;
  const deposit = depositRequired || dryLeaseTerms?.depositRequired;
  const minPeriod = minimumPeriod || dryLeaseTerms?.minimumPeriod;
  const maxPeriod = maximumPeriod || dryLeaseTerms?.maximumPeriod;

  // Extract from nested dryLeaseTerms if available (backwards compatibility)
  const maintenance = maintenanceResponsibility || dryLeaseTerms?.maintenanceResponsibility;
  const partsPayment = partsRepairsPayment || dryLeaseTerms?.partsAndRepairsPayment || dryLeaseTerms?.partsRepairsPayment;
  const returnCond = returnConditions || dryLeaseTerms?.returnConditions;
  const maxUsage = maxUsageHoursPerMonth || dryLeaseTerms?.maxUsageHoursPerMonth;
  const insurance = insuranceCoverageRequired || dryLeaseTerms?.insuranceCoverageRequired;
  const registration = registrationRequirements || dryLeaseTerms?.registrationRequirements;
  const inspection = inspectionRights || dryLeaseTerms?.inspectionRights;
  const termination = earlyTerminationFees || dryLeaseTerms?.earlyTerminationFees;
  
  // Availability data
  const availabilityStatus = availability || dryLeaseTerms?.availability || [];
  const availFrom = availableFromDate || dryLeaseTerms?.availableFromDate;
  const availUntil = availableUntilDate || dryLeaseTerms?.availableUntilDate;
  
  // Delivery data
  const delivery = deliveryOptions || dryLeaseTerms?.deliveryOptions || [];
  
  // Operational requirements
  const pilotQuals = pilotQualifications || [];
  const insuranceReqs = insuranceRequirements || [];
  const operatingLimits = operatingLimitations || [];
  
  // Emergency/support
  const emergency = emergencyContact || dryLeaseTerms?.emergencyContact;
  const breakdown = breakdownProcedures || dryLeaseTerms?.breakdownProcedures;

  // Check for pricing data
  const hasPricingData = hourly || monthly || deposit || minPeriod || maxPeriod;
  const hasAvailabilityData = availabilityStatus.length > 0 || availFrom || availUntil;
  const hasDeliveryData = delivery.length > 0;
  const hasOperationalReqs = pilotQuals.length > 0 || insuranceReqs.length > 0 || operatingLimits.length > 0;
  const hasEmergencyData = emergency || breakdown;

  // Don't render if no data at all
  const hasData = hasPricingData || maintenance || partsPayment || returnCond || maxUsage || 
                  insurance || registration || inspection || termination ||
                  hasAvailabilityData || hasDeliveryData || hasOperationalReqs || hasEmergencyData;
  
  if (!hasData) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };
  
  const getDeliveryLabel = (option: string) => {
    const labels: Record<string, string> = {
      'base_included': 'Delivery at base (included)',
      'deliver_to_lessee': 'Delivery to lessee location (additional cost)',
      'ferry_pilot': 'Ferry pilot services available',
      'training_available': 'Training services available'
    };
    return labels[option] || option;
  };
  
  const getPartsPaymentLabel = (value: string) => {
    const labels: Record<string, string> = {
      'lessee': 'Lessee (Renter) Pays All',
      'lessor': 'Lessor (Owner) Pays All',
      'shared': 'Shared Responsibility',
      'major_lessor': 'Lessee pays routine, Lessor pays major overhauls'
    };
    return labels[value] || value;
  };
  
  const getMaintenanceLabel = (value: string) => {
    const labels: Record<string, string> = {
      'lessee': 'Lessee (Renter) Responsible',
      'lessor': 'Lessor (Owner) Responsible',
      'shared': 'Shared Responsibility'
    };
    return labels[value] || value;
  };

  const InfoCard = ({ icon: Icon, title, value, variant = 'default' }: any) => {
    if (!value) return null;
    
    return (
      <Card className={`border-2 ${variant === 'highlight' ? 'border-primary/20 bg-primary/5' : ''}`}>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-1">
              <Icon className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-muted-foreground mb-1">{title}</div>
              <div className="text-sm text-foreground">{value}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className={`space-y-6 ${className}`}>
      <Card className="gradient-card border-2">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <FileText className="w-6 h-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl">Dry Lease Terms & Conditions</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Rates, availability, maintenance, and operational requirements
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Lease Rates & Terms Section */}
          {hasPricingData && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <Banknote className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-base">Lease Rates & Terms</h3>
              </div>
              {hidePrice ? (
                <BlurredPrivateData
                  placeholderContent={
                    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                      <div className="h-24 bg-muted rounded-lg border-2" />
                      <div className="h-24 bg-muted rounded-lg border-2" />
                      <div className="h-24 bg-muted rounded-lg border-2" />
                    </div>
                  }
                  fieldName="Lease Rates"
                  size="lg"
                  onRequestInfo={onRequestInfo}
                />
              ) : (
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {monthly && (
                    <InfoCard
                      icon={DollarSign}
                      title="Monthly Rate"
                      value={`${formatCurrency(monthly)}/month`}
                      variant="highlight"
                    />
                  )}
                  {hourly && (
                    <InfoCard
                      icon={Clock}
                      title="Hourly Rate"
                      value={`${formatCurrency(hourly)}/hr`}
                      variant="highlight"
                    />
                  )}
                  {deposit && (
                    <InfoCard
                      icon={Banknote}
                      title="Security Deposit"
                      value={formatCurrency(deposit)}
                    />
                  )}
                  {(minPeriod || maxPeriod) && (
                    <InfoCard
                      icon={Calendar}
                      title="Lease Period"
                      value={minPeriod && maxPeriod 
                        ? `${minPeriod} - ${maxPeriod} months` 
                        : minPeriod 
                          ? `Min: ${minPeriod} months` 
                          : `Max: ${maxPeriod} months`}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {/* Aircraft Availability Section */}
          {hasAvailabilityData && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <HelicopterSilhouetteIcon className="w-5 h-5" />
                <h3 className="font-semibold text-base">Aircraft Availability</h3>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                {availabilityStatus.length > 0 && (
                  <InfoCard
                    icon={Clock}
                    title="Availability Status"
                    value={availabilityStatus.join(', ')}
                    variant="highlight"
                  />
                )}
                {(availFrom || availUntil) && (
                  <InfoCard
                    icon={Calendar}
                    title="Available Dates"
                    value={availFrom && availUntil 
                      ? `${formatDate(availFrom)} - ${formatDate(availUntil)}`
                      : availFrom 
                        ? `From: ${formatDate(availFrom)}`
                        : `Until: ${formatDate(availUntil)}`}
                  />
                )}
              </div>
            </div>
          )}

          {/* Delivery Options Section */}
          {hasDeliveryData && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <Truck className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-base">Delivery Options</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {delivery.map((option: string, index: number) => (
                  <Badge key={index} variant="secondary" className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    {getDeliveryLabel(option)}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Maintenance Section */}
          {(maintenance || partsPayment) && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <Wrench className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-base">Maintenance & Repairs</h3>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <InfoCard
                  icon={Wrench}
                  title="Maintenance Responsibility"
                  value={getMaintenanceLabel(maintenance)}
                  variant="highlight"
                />
                <InfoCard
                  icon={DollarSign}
                  title="Parts & Repairs Payment"
                  value={getPartsPaymentLabel(partsPayment)}
                />
              </div>
            </div>
          )}

          {/* Usage & Operations Section */}
          {(maxUsage || returnCond) && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-base">Usage & Operations</h3>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                {maxUsage && (
                  <InfoCard
                    icon={Clock}
                    title="Maximum Usage Per Month"
                    value={`${maxUsage} hours`}
                  />
                )}
                {returnCond && (
                  <InfoCard
                    icon={CheckCircle}
                    title="Return Conditions"
                    value={returnCond}
                  />
                )}
              </div>
            </div>
          )}

          {/* Operational Requirements Section */}
          {hasOperationalReqs && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-base">Operational Requirements</h3>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                {pilotQuals.length > 0 && (
                  <InfoCard
                    icon={Users}
                    title="Pilot Qualifications"
                    value={pilotQuals.join('; ')}
                  />
                )}
                {insuranceReqs.length > 0 && (
                  <InfoCard
                    icon={Shield}
                    title="Insurance Requirements"
                    value={insuranceReqs.join('; ')}
                  />
                )}
                {operatingLimits.length > 0 && (
                  <InfoCard
                    icon={AlertCircle}
                    title="Operating Limitations"
                    value={operatingLimits.join('; ')}
                  />
                )}
              </div>
            </div>
          )}

          {/* Insurance & Registration Section */}
          {(insurance || registration) && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-base">Insurance & Registration</h3>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <InfoCard
                  icon={Shield}
                  title="Insurance Coverage Required"
                  value={typeof insurance === 'number' ? formatCurrency(insurance) : insurance}
                  variant="highlight"
                />
                <InfoCard
                  icon={FileText}
                  title="Registration Requirements"
                  value={registration}
                />
              </div>
            </div>
          )}

          {/* Inspection & Oversight Section */}
          {inspection && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <Eye className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-base">Inspection Rights</h3>
              </div>
              <InfoCard
                icon={Eye}
                title="Lessor Inspection Rights"
                value={inspection}
              />
            </div>
          )}

          {/* Emergency & Support Section */}
          {hasEmergencyData && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <Phone className="w-5 h-5 text-red-500" />
                <h3 className="font-semibold text-base">Emergency & Support</h3>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                {emergency && (
                  <InfoCard
                    icon={Phone}
                    title="24/7 Emergency Contact"
                    value={emergency}
                    variant="highlight"
                  />
                )}
                {breakdown && (
                  <InfoCard
                    icon={Wrench}
                    title="Breakdown & Maintenance Support"
                    value={breakdown}
                  />
                )}
              </div>
            </div>
          )}

          {/* Termination Section */}
          {termination && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-base">Early Termination</h3>
              </div>
              <InfoCard
                icon={AlertCircle}
                title="Early Termination Fees"
                value={termination}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DryLeaseDetailsDisplay;